/**
 * Account usernames: unique across all players, so they are kept simple
 * (latin letters, digits, underscore) and compared case-insensitively.
 */

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 20;
const USERNAME_PATTERN = /^[A-Za-z0-9_]+$/;

export type UsernameProblem = 'too_short' | 'too_long' | 'bad_characters';

export function usernameProblem(name: string): UsernameProblem | null {
  if (name.length < USERNAME_MIN) return 'too_short';
  if (name.length > USERNAME_MAX) return 'too_long';
  if (!USERNAME_PATTERN.test(name)) return 'bad_characters';
  return null;
}

/** The key that must be unique: "CesurPanda" and "cesurpanda" are the same player. */
export function usernameKey(name: string): string {
  return name.toLowerCase();
}

// Turkish words without special letters so every name stays within the allowed characters.
const WORDS = {
  tr: {
    adjectives: ['Neseli', 'Cesur', 'Hizli', 'Akilli', 'Minik', 'Sirin', 'Sevimli', 'Kurnaz', 'Uykucu', 'Merakli', 'Komik', 'Parlak'],
    animals: ['Panda', 'Tavsan', 'Kedi', 'Penguen', 'Zurafa', 'Kirpi', 'Sincap', 'Yunus', 'Aslan', 'Fil', 'Koala', 'Ordek'],
  },
  en: {
    adjectives: ['Happy', 'Brave', 'Speedy', 'Clever', 'Tiny', 'Sunny', 'Sneaky', 'Sleepy', 'Curious', 'Funny', 'Shiny', 'Jolly'],
    animals: ['Panda', 'Bunny', 'Kitten', 'Penguin', 'Giraffe', 'Hedgehog', 'Squirrel', 'Dolphin', 'Lion', 'Elephant', 'Koala', 'Duck'],
  },
} as const;

export const USERNAME_WORDS = WORDS;

/** e.g. "CesurPanda42". Two digits keep collisions rare; the server still checks. */
export function randomUsername(locale: 'tr' | 'en', random: () => number = Math.random): string {
  const { adjectives, animals } = WORDS[locale];
  const pick = (list: readonly string[]) => list[Math.floor(random() * list.length)];
  const digits = String(10 + Math.floor(random() * 90));
  return `${pick(adjectives)}${pick(animals)}${digits}`;
}
